import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled, { css } from 'styled-components'
import { StyledTextArea } from 'components/form-components/styled-text-area'
import { Button } from 'components/styled/button'
import { Flex } from 'components/styled/containers/flex'
import { MenuButton } from 'components/styled/menu/menu-item'
import { Body, H3, Subtitle } from 'components/styled/typography'
import Html from 'components/html/html'
import { InterviewGuideStepType } from 'types'
import debounce from 'lodash/debounce'
import { I18nComponentType } from 'i18n-types'
import { mapLanguageToComponentProps } from 'i18n'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'

interface StructuredInterviewProps {
  interviewGuideSteps: any // TODO: change type when we have actual structure
  children: React.ReactNode
  i18n: I18nComponentType
  view?: 'pre-call' | 'in-call'
  interviewTitle?: string
  candidate: { name?: string; email?: string }
  saveNotes: (stepId: string, noteType: string, notes: string) => void
  userEmail: string
  guideModel: string
  dispatch: any
  timeLeft?: string
}

const StyledContainer = styled.div<{ $colorTheme: 'light' | 'dark' }>`
  display: flex;
  flex-direction: column;
  background: ${({ $colorTheme, theme }) =>
    $colorTheme === 'light' ? theme.color.white : theme.color.black}};
  padding: ${({ theme }) => theme.space.s16};

  ::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 7px;
}
::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, .5);
  -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, .5);
}
`

const ProgressBar = styled.div`
  width: 100%;
  height: ${({ theme }) => theme.space.s12};
  background: ${({ theme }) => theme.color.grey10};
  border-radius: ${({ theme }) => theme.space.s4};
  overflow: hidden;
  position: relative;
`

const ProgressFiller = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${({ $percent }) => `${$percent}%`};
  background: ${({ theme }) => theme.color.primary110};
  transition: width 0.3s ease;
  position: absolute;
  left: 0;
  top: 0;
`
const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.space.s8};

  ${({ theme }) => theme.mediaQuery.tablet} {
    flex-direction: column;
  }
`

const ViewContainer = styled(Flex)`
  gap: ${({ theme }) => theme.space.s24};
  height: 100%;
  width: 100%;
  justify-content: space-around;

  ${({ theme }) => theme.mediaQuery.tablet} {
    flex-direction: column-reverse;
  }
`

const StepWrapper = styled.div<{ $view: 'pre-call' | 'in-call' }>`
  width: ${({ $view }) => ($view === 'pre-call' ? '50%' : '40%')};

  ${({ theme }) => theme.mediaQuery.tablet} {
    width: 100%;
  }
`

const StepContainer = styled.div<{ $view?: 'pre-call' | 'in-call' }>`
  border-radius: ${({ theme }) => theme.space.s4};
  padding: ${({ theme }) => theme.space.s12};
  background-color: ${({ theme }) => theme.color.grey05};
  height: fit-content;
  height: 55vh;
  overflow: scroll;

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.5);
    -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }

  ${({ theme }) => theme.mediaQuery.tablet} {
    width: 100%;
    height: ${({ theme }) => theme.space.s320};
  }
`

const StyledSection = styled(Flex)`
  border-bottom: 1px solid ${({ theme }) => theme.color.grey10};
  padding: ${({ theme }) => theme.space.s16} 0;

  &:last-of-type {
    border-bottom: none;
  }
`

const StyledStepButton = styled(MenuButton)<{ $isSelected?: boolean }>`
  width: 100%;
  button {
    > * {
      text-align: center;
    }
    color: ${({ theme }) => theme.color.black} !important;
    flex: 1;
    min-width: 0;

    background: ${({ theme }) => theme.color.grey05};
    border-radius: ${({ theme }) => theme.space.s4};
    width: 100%;
    display: flex;
    align-items: center;
    background-color: ${({ theme }) => theme.color.primary05} !important;
    color: ${({ theme }) => theme.color.primary110} !important;

    ${({ $isSelected }) =>
      $isSelected &&
      css`
        background-color: ${({ theme }) => theme.color.primary110} !important;
        color: ${({ theme }) => theme.color.white} !important;
      `}

    &:hover {
      background-color: ${({ theme }) => theme.color.primary120} !important;
      color: ${({ theme }) => theme.color.white} !important;
    }

    &.focus-visible {
      border-color: ${({ theme }) => theme.color.primary110} !important;
    }

    &:active {
      background-color: ${({ theme }) => theme.color.primary120} !important;
      color: ${({ theme }) => theme.color.white} !important;
    }
  }
`

const ButtonsContainer = styled.div`
  overflow: scroll;
  display: flex;
  justify-content: stretch;
  gap: ${({ theme }) => theme.space.s8};
  white-space: nowrap;
  margin-bottom: ${({ theme }) => theme.space.s16};

  ${({ theme }) => theme.mediaQuery.tablet} {
    flex-direction: column;
    margin-bottom: 0;
  }
`

const ButtonWrapper = styled(Flex)`
  flex: 1;
`

const CallContainer = styled.div<{ $view: 'pre-call' | 'in-call' }>`
  width: ${({ $view }) => ($view === 'pre-call' ? '50%' : '60%')};

  ${({ theme }) => theme.mediaQuery.tablet} {
    width: 100%;
  }
`

const NotesContainer = styled.div`
  margin-top: ${({ theme }) => theme.space.s16};
  width: 100%;
  display: flex;
  align-items: flex-start;
`

const NameInfoContainer = styled(Flex)`
  padding-right: ${({ theme }) => theme.space.s16};
  border-right: 1px solid ${({ theme }) => theme.color.grey20};
`

const TimeContainer = styled(Flex)`
  gap: ${({ theme }) => theme.space.s8};
  align-items: center;
  justify-content: center;
`

const StructuredInterview: React.FC<StructuredInterviewProps> = ({
  interviewGuideSteps,
  children,
  i18n,
  view = 'pre-call',
  candidate,
  interviewTitle,
  saveNotes,
  userEmail,
  guideModel,
  timeLeft,
  dispatch,
}) => {
  const [selectedStepIndex, setSelectedStepIndex] = useState<number>(0)
  const [notes, setNotes] = useState<string>('')
  const [theme, setTheme] = useState<'dark' | 'light'>('dark') // TODO: remove is no need for 2 color themes
  const [elapsedSecondsPerStep, setElapsedSecondsPerStep] = useState<{
    [key: number]: number
  }>({})
  const [totalElapsedSeconds, setTotalElapsedSeconds] = useState(0)
  const [totalDurationSeconds, setTotalDurationSeconds] = useState(0)
  const [notesEdited, setNotesEdited] = useState(false)
  const timerRef = useRef<null | ReturnType<typeof setInterval>>(null)
  const stepElapsedSeconds = elapsedSecondsPerStep[selectedStepIndex] || 0
  const minutesForStep = Math.floor(stepElapsedSeconds / 60)
  const secondsForStep = stepElapsedSeconds % 60
  const totalElapsedMinutes = Math.floor(totalElapsedSeconds / 60)
  const totalDurationMinutes = Math.floor(totalDurationSeconds / 60)
  const selectedStep =
    selectedStepIndex !== null ? interviewGuideSteps[selectedStepIndex] : null
  // TODO save notes when typing stops

  const getElapsedTimeColor = (
    minutesElapsed: number,
    totalMinutes?: number
  ) => {
    const textColor = theme === 'light' ? 'bodyText' : 'white'
    if (!totalMinutes) return textColor
    const elapsedRatio = minutesElapsed / totalMinutes
    if (elapsedRatio < 0.8) return textColor
    if (elapsedRatio < 1) return 'warn110'
    if (elapsedRatio >= 1) return 'error110'
    return textColor
  }

  const elapsedStepTimeColor = getElapsedTimeColor(
    minutesForStep,
    selectedStep?.timeEstimate
  )

  const elapsedTotalTimeColor = getElapsedTimeColor(
    totalElapsedMinutes,
    totalDurationMinutes
  )

  const noteIndex = selectedStep
    ? selectedStep.duringInterviewNotes.findIndex(
        (note: { interviewer: string; notes: string }) =>
          note.interviewer === userEmail
      )
    : -1

  const updateNotes = (newNotes: string) => {
    setNotes(newNotes)
    setNotesEdited(true)
    if (noteIndex > -1) {
      // Update existing note
      dispatch(
        actions.change(
          `${guideModel}.steps[${selectedStepIndex}].duringInterviewNotes[${noteIndex}].notes`,
          newNotes
        )
      )
    } else {
      // Add new note
      dispatch(
        actions.push(
          `${guideModel}.steps[${selectedStepIndex}].duringInterviewNotes`,
          { interviewer: userEmail, notes: newNotes }
        )
      )
    }
  }

  const debounceSaveNotes = useCallback(
    debounce((stepId, notesToSave) => {
      saveNotes(stepId, 'duringInterviewNotes', notesToSave)
      setNotesEdited(false)
    }, 700),
    []
  )

  useEffect(() => {
    const selectedStep = interviewGuideSteps?.[selectedStepIndex]
    if (selectedStep) {
      const userInterviewNotes =
        selectedStep.duringInterviewNotes.find(
          (note: { interviewer: string; notes: string }) =>
            note.interviewer === userEmail
        )?.notes || ''

      setNotes(userInterviewNotes)
    }
  }, [selectedStepIndex, interviewGuideSteps])

  useEffect(() => {
    if (notesEdited && selectedStep && selectedStep._id) {
      debounceSaveNotes(selectedStep._id, notes)
    }
  }, [notes])

  useEffect(() => {
    const notesArray = selectedStep.duringInterviewNotes || []
    const noteIndex = notesArray.findIndex(
      (n: { interviewer: string; notes: string }) => n.interviewer === userEmail
    )
    if (noteIndex === -1) {
      dispatch(
        actions.push(
          `${guideModel}.steps[${selectedStepIndex}].duringInterviewNotes`,
          { interviewer: userEmail, notes: '' }
        )
      )
    }
  }, [selectedStepIndex, userEmail])

  useEffect(() => {
    if (view === 'in-call') {
      setSelectedStepIndex(0)
    }
  }, [view])

  useEffect(() => {
    if (view === 'in-call') {
      const sumofEstimates = interviewGuideSteps.reduce(
        (acc: number, step: InterviewGuideStepType) =>
          acc + (step.timeEstimate || 0) * 60,
        0
      )
      setTotalDurationSeconds(sumofEstimates)
    }
  }, [view, interviewGuideSteps])

  useEffect(() => {
    if (view === 'in-call' && !timeLeft) {
      if (timerRef.current) clearInterval(timerRef.current)
      timerRef.current = setInterval(() => {
        setElapsedSecondsPerStep((prev) => ({
          ...prev,
          [selectedStepIndex]: (prev[selectedStepIndex] || 0) + 1,
        }))
      }, 1000)
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [view, selectedStepIndex, timeLeft])

  // Calculate elapsed time in seconds
  useEffect(() => {
    if (view === 'in-call') {
      const elapsedBeforeCurrentStep = Object.entries(elapsedSecondsPerStep)
        .filter(([key]) => parseInt(key, 10) < selectedStepIndex)
        .reduce((acc, [, value]) => acc + value, 0)
      const currentStepElapsed = elapsedSecondsPerStep[selectedStepIndex] || 0

      setTotalElapsedSeconds(elapsedBeforeCurrentStep + currentStepElapsed)
    }
  }, [selectedStepIndex, elapsedSecondsPerStep, interviewGuideSteps])

  useEffect(() => {
    if (interviewGuideSteps.length > 0) {
      setSelectedStepIndex(0)
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  /*   const progressPercent = Math.min(
    (totalElapsedSeconds / totalDurationSeconds) * 100,
    100
  ) */

  const hasNextStep = selectedStepIndex < interviewGuideSteps.length - 1

  const stepButtons = (
    <Flex flexDirection="column" gap="s8">
      {/*{view === 'in-call' && (
        <ProgressBar>
          <ProgressFiller $percent={progressPercent} />
        </ProgressBar>
      )} */}
      <ButtonsContainer>
        {interviewGuideSteps.map((step: any, index: number) => {
          return (
            <ButtonWrapper
              flexDirection="column"
              alignItems="center"
              gap="s8"
              key={index}
            >
              <StyledStepButton
                text={step.title}
                onClick={() => setSelectedStepIndex(index)}
                $isSelected={index === selectedStepIndex}
                variant="basic"
              />
              <Body
                fontWeight={400}
                color={theme === 'light' ? 'bodyText' : 'white'}
              >
                {i18n.liveRoom.structuredInterview.timeEstimateText(
                  step.timeEstimate
                )}
              </Body>
            </ButtonWrapper>
          )
        })}
      </ButtonsContainer>
    </Flex>
  )

  const stepContent = selectedStep ? (
    <StepWrapper $view={view}>
      <StepContainer $view={view}>
        <H3 mb="s16">{`${selectedStep.title} ${selectedStep.timeEstimate} min`}</H3>
        <StyledSection flexDirection="column" gap="s12" alignItems="flex-start">
          <Html html={selectedStep.duringInterviewContent} />
        </StyledSection>
      </StepContainer>
      <Flex
        alignItems="center"
        gap="s8"
        justifyContent="space-between"
        mt="s12"
      >
        <Body
          color={theme === 'light' ? 'bodyText' : 'white'}
          numberOfLines={2}
        >
          {hasNextStep &&
            i18n.liveRoom.structuredInterview.nextStep(
              interviewGuideSteps[selectedStepIndex + 1]?.title
            )}
        </Body>
        {hasNextStep && (
          <Button
            variant="primary"
            size="small"
            iconRight="arrow_forward"
            text={i18n.liveRoom.structuredInterview.goToNextStepButtonText}
            onClick={() => setSelectedStepIndex(selectedStepIndex + 1)}
          />
        )}
      </Flex>
      <NotesContainer>
        {/** TODO: update directly to live and get from redux? and do a debounce change */}
        <StyledTextArea
          placeholder={i18n.liveRoom.structuredInterview.guideNotesPlaceHolder(
            selectedStep.title
          )}
          model={
            noteIndex && noteIndex > -1
              ? `${guideModel}.steps[${selectedStepIndex}].duringInterviewNotes[${noteIndex}].notes`
              : undefined
          }
          rows={5}
          value={notes}
          onChange={(e: any) => {
            updateNotes(e.target.value)
          }}
        />
      </NotesContainer>
    </StepWrapper>
  ) : null
  const infoContainer = (
    <Flex
      mt="s12"
      mb="s16"
      gap="s16"
      justifyContent="flex-start"
      alignItems="center"
    >
      <NameInfoContainer
        flexDirection="column"
        alignItems="flex-start"
        gap="s4"
      >
        {candidate?.name && (
          <H3 color={theme === 'light' ? 'bodyText' : 'white'} mb="s0">
            {candidate?.name}
          </H3>
        )}
        <Subtitle
          mb="s0"
          fontWeight={400}
          color={theme === 'light' ? 'bodyText' : 'white'}
        >
          {candidate?.email}
        </Subtitle>
      </NameInfoContainer>
      {interviewTitle && (
        <H3
          mb="s0"
          fontWeight={400}
          color={theme === 'light' ? 'bodyText' : 'white'}
        >
          {interviewTitle}
        </H3>
      )}
    </Flex>
  )

  const timeInfo = () => {
    if (timeLeft) {
      return (
        <Subtitle mb="s0" color="white" fontWeight={400}>
          {i18n.liveRoom.interviewStartsIn(timeLeft)}
        </Subtitle>
      )
    }

    if (view === 'in-call') {
      return (
        <Flex flexDirection="column">
          <Flex justifyContent="space-between" gap="s12">
            <Body
              mb="s0"
              color={theme === 'light' ? 'bodyText' : 'white'}
              fontWeight={400}
            >
              {i18n.liveRoom.structuredInterview.timeIn(selectedStep?.title)}
            </Body>
            <TimeContainer>
              <Body color={elapsedStepTimeColor} mb="s0">
                {`${minutesForStep} min ${secondsForStep} s`}
              </Body>
              <Body mb="s0" color={theme === 'light' ? 'bodyText' : 'white'}>
                /
              </Body>
              <Body mb="s0" color={theme === 'light' ? 'bodyText' : 'white'}>
                {`${selectedStep?.timeEstimate} min`}
              </Body>
            </TimeContainer>
          </Flex>
          <Flex justifyContent="space-between" gap="s12">
            <Body
              mb="s0"
              color={theme === 'light' ? 'bodyText' : 'white'}
              fontWeight={400}
            >
              {i18n.liveRoom.structuredInterview.totalTime}
            </Body>
            <TimeContainer>
              <Body mb="s0" color={elapsedTotalTimeColor}>
                {`${totalElapsedMinutes} min`}
              </Body>
              <Body mb="s0" color={theme === 'light' ? 'bodyText' : 'white'}>
                /
              </Body>
              <Body mb="s0" color={theme === 'light' ? 'bodyText' : 'white'}>
                {`${totalDurationMinutes} min`}
              </Body>
            </TimeContainer>
          </Flex>
        </Flex>
      )
    }
    return null
  }

  return (
    <StyledContainer $colorTheme={theme}>
      <StyledHeader>
        {infoContainer}
        {timeInfo()}
      </StyledHeader>
      {stepButtons}
      <ViewContainer>
        {stepContent}
        <CallContainer $view={view}>{children}</CallContainer>
      </ViewContainer>
    </StyledContainer>
  )
}

export default connect(mapLanguageToComponentProps())(StructuredInterview)
